// src/components/AdminPanel.jsx
import React, { useEffect, useState, useRef, Fragment } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  MapContainer,
  TileLayer,
  FeatureGroup,
  Polyline,
  Marker,
  Popup,
  useMap
} from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import 'leaflet-polylinedecorator';
import {
  collection,
  onSnapshot,
  addDoc,
  deleteDoc,
  updateDoc,
  doc,
  writeBatch
} from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { db, auth } from '../firebaseConfig';
import Cookies from 'js-cookie';
import L from 'leaflet';

// Importación de datos estáticos para migración
import mapData from '../datos/mapData.json';
import direccionesData from '../datos/direcciones.json';

// Íconos
const alertaIcon = L.icon({
  iconUrl: '/iconos/alerta.png',
  iconSize: [25, 25],
  iconAnchor: [12, 12],
});
const puntoRecogidaIcon = L.icon({
  iconUrl: '/iconos/recogida.png',
  iconSize: [25, 25],
  iconAnchor: [12, 12],
});
const hotelIcon = L.icon({
  iconUrl: '/iconos/hotel.png',
  iconSize: [25, 25],
  iconAnchor: [12, 12],
});
// Nuevo icono para Hotel En Vial (ruta vacía)
const hotelVialIcon = L.icon({
  iconUrl: '/iconos/hotel_azul.png', // ← pon aquí la ruta de tu hotel_negro_vial.png
  iconSize: [32, 32],
  iconAnchor: [12, 12],
});

// Componente para flechas en polyline
const ArrowedLine = ({ positions }) => {
  const map = useMap();
  useEffect(() => {
    const poly = L.polyline(positions, { opacity: 0 }).addTo(map);
    const decorator = L.polylineDecorator(poly, {
      patterns: [{
        offset: 40,
        repeat: 300,
        symbol: L.Symbol.arrowHead({
          pixelSize: map.getZoom() * 0.7,
          polygon: false,
          pathOptions: { stroke: true, weight: 3, color: '#000', opacity: 1 }
        })
      }]
    }).addTo(map);
    return () => {
      map.removeLayer(decorator);
      map.removeLayer(poly);
    };
  }, [map, positions]);
  return null;
};

const AdminPanel = () => {
  const navigate = useNavigate();

  // Estados
  const [rutas, setRutas] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [hoteles, setHoteles] = useState([]);
  const [direcciones, setDirecciones] = useState([]);

  const [selectedLineColor, setSelectedLineColor] = useState('green');
  const [selectedMarkerType, setSelectedMarkerType] = useState('alerta');

  // Estados de migración
  const [isMigrating, setIsMigrating] = useState(false);
  const [migrationProgress, setMigrationProgress] = useState(0);
  const [migrationTotal, setMigrationTotal] = useState(0);

  const featureGroupRef = useRef(null);
  const center = [39.70241114681138, 2.9437189174222302];

  // Validación de sesión/admin
  useEffect(() => {
    const cu = Cookies.get('currentUser'), dv = Cookies.get('deviceUid');
    if (!cu || !dv) return navigate('/');
    let obj;
    try { obj = JSON.parse(cu); } catch { return navigate('/'); }
    const allowed = ["admimanuel", "adminjose", "admindani"];
    if (!obj.id || !allowed.includes(obj.usuario)) return navigate('/');
    const uref = doc(db, 'usuarios', obj.id);
    const unsub = onSnapshot(uref, snap => {
      if (!snap.exists()) {
        console.error("El documento de usuario no existe en Firestore");
        Cookies.remove('currentUser');
        navigate('/');
        return;
      }
      const data = snap.data();
      if (data.deviceUid !== dv) {
        console.warn("Conflicto de deviceUid: Firestore=" + data.deviceUid + ", Cookie=" + dv);
        // Para administradores, esto ya no debería expulsarles si LogPrueba actualiza el deviceUid
        // Pero mantenemos la validación por seguridad si el cambio es inesperado
        Cookies.remove('currentUser');
        navigate('/');
      }
    }, (err) => {
      console.error("Error en onSnapshot de validación:", err);
      navigate('/');
    });
    return () => unsub();
  }, [navigate]);

  // Listeners Firestore
  useEffect(() => {
    const unsubR = onSnapshot(collection(db, 'rutas'),
      s => setRutas(s.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    const unsubA = onSnapshot(collection(db, 'alertas'),
      s => setAlertas(s.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    const unsubH = onSnapshot(collection(db, 'hoteles'),
      s => setHoteles(s.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    const unsubD = onSnapshot(collection(db, 'direcciones'),
      s => setDirecciones(s.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return () => { unsubR(); unsubA(); unsubH(); unsubD(); };
  }, []);

  // Al crear shapes
  const onCreated = async ({ layerType, layer }) => {
    // Flecha
    if (layerType === 'polyline' && selectedMarkerType === 'flecha') {
      const latlngs = layer.getLatLngs();
      if (latlngs.length < 2) {
        alert('Dibuja al menos 2 puntos para la flecha');
        return;
      }
      const coords = latlngs.map(ll => ({ lat: ll.lat, lng: ll.lng }));
      try {
        await addDoc(collection(db, 'direcciones'), { coords });
        featureGroupRef.current.removeLayer(layer);
      } catch (err) {
        console.error(err);
      }
      return;
    }

    // Rutas
    if (layerType === 'polyline') {
      const latlngs = layer.getLatLngs();
      let tipo = 'segura';
      if (selectedLineColor === 'yellow') tipo = 'advertencia';
      if (selectedLineColor === 'red') tipo = 'prohibida';
      if (selectedLineColor === 'blue') tipo = 'informativa';
      try {
        const ref = await addDoc(collection(db, 'rutas'), {
          tipo,
          coordenadas: latlngs.map(ll => ({ lat: ll.lat, lng: ll.lng }))
        });
        layer.options.docId = ref.id;
      } catch (err) {
        console.error(err);
      }
      return;
    }

    // Marcadores (alertas, punto de recogida, hotel, hotel_vial)
    if (layerType === 'marker' && selectedMarkerType !== 'flecha') {
      const { lat, lng } = layer.getLatLng();
      // Hoteles normales y Vial
      if (selectedMarkerType === 'hotel' || selectedMarkerType === 'hotel_vial') {
        const nombre = prompt('Nombre del hotel', 'Hotel');
        if (!nombre) return;
        await addDoc(collection(db, 'hoteles'), {
          nombre,
          lat,
          lng,
          tipo: selectedMarkerType  // guarda como 'hotel' o 'hotel_vial'
        });
        return;
      }
      // Alertas y puntos de recogida
      const title = prompt('Título', 'Título');
      const description = prompt('Descripción', 'Descripción');
      await addDoc(collection(db, 'alertas'), {
        tipo: selectedMarkerType,
        coordenadas: { lat, lng },
        title,
        description
      });
    }
  };

  // Eliminar flecha
  const handleDeleteFlecha = async id => {
    await deleteDoc(doc(db, 'direcciones', id));
  };

  // Migración JSON -> Firebase (Optimizado con Batches)
  const handleMigrateData = async () => {
    if (!window.confirm("¿Seguro que quieres migrar los datos? Esto subirá ~2300 objetos mediante lotes de alta velocidad.")) return;

    setIsMigrating(true);
    setMigrationProgress(0);
    
    // Calcular total de operaciones
    const totalOps = mapData.lines.length + mapData.points.length + Object.keys(direccionesData).length;
    setMigrationTotal(totalOps);

    try {
      const allItems = [];

      // 1. Preparar Líneas
      mapData.lines.forEach(line => {
        let tipo = 'segura';
        if (line.color === 'yellow') tipo = 'advertencia';
        if (line.color === 'red') tipo = 'prohibida';
        if (line.color === 'blue') tipo = 'informativa';
        allItems.push({ coll: 'rutas', data: { tipo, coordenadas: line.positions.map(p => ({ lat: p[0], lng: p[1] })) } });
      });

      // 2. Preparar Puntos
      mapData.points.forEach(point => {
        if (point.type === 'hotel') {
          allItems.push({ coll: 'hoteles', data: { nombre: point.name, lat: point.position[0], lng: point.position[1], tipo: point.isVial ? 'hotel_vial' : 'hotel' } });
        } else {
          allItems.push({ coll: 'alertas', data: { tipo: point.type === 'advertencia' ? 'advertencia' : 'informativa', coordenadas: { lat: point.position[0], lng: point.position[1] }, title: point.name, description: point.description } });
        }
      });

      // 3. Preparar Flechas
      Object.entries(direccionesData).forEach(([id, val]) => {
        allItems.push({ coll: 'direcciones', data: { coords: val.coords } });
      });

      // Subir en lotes de 500
      let processed = 0;
      for (let i = 0; i < allItems.length; i += 500) {
        const batch = writeBatch(db);
        const chunk = allItems.slice(i, i + 500);
        
        chunk.forEach(item => {
          const docRef = doc(collection(db, item.coll));
          batch.set(docRef, item.data);
        });

        await batch.commit();
        processed += chunk.length;
        setMigrationProgress(processed);
      }

      alert("¡Migración completada con éxito en modo ráfaga!");
    } catch (err) {
      console.error("Error en migración por lotes:", err);
      alert("Hubo un error durante la migración.");
    } finally {
      setIsMigrating(false);
    }
  };

  // Exportación Firebase -> JSON (mapData.json)
  const handleExportJSON = () => {
    const exportedLines = rutas.map(r => ({
      id: r.id,
      positions: r.coordenadas.map(c => [c.lat, c.lng]),
      color: getColor(r.tipo)
    }));

    const exportedPoints = [
      ...hoteles.map(h => ({
        id: h.id,
        type: 'hotel',
        position: [h.lat, h.lng],
        name: h.nombre,
        description: "",
        isVial: h.tipo === 'hotel_vial'
      })),
      ...alertas.map(a => ({
        id: a.id,
        type: a.tipo,
        position: [a.coordenadas.lat, a.coordenadas.lng],
        name: a.title,
        description: a.description,
        isVial: false
      }))
    ];

    const fullData = {
      lines: exportedLines,
      arrows: [], // Las flechas se cargan desde direcciones.json o se integran aquí si prefieres
      points: exportedPoints,
      exportedAt: new Date().toISOString(),
      version: "2.1"
    };

    const blob = new Blob([JSON.stringify(fullData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'mapData.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  // Logout
  const handleLogout = async () => {
    await signOut(auth);
    Cookies.remove('currentUser');
    navigate('/');
  };

  const getColor = tipo => {
    switch (tipo) {
      case 'segura': return 'green';
      case 'advertencia': return 'yellow';
      case 'prohibida': return 'red';
      case 'informativa': return 'blue';
      default: return 'blue';
    }
  };

  return (
    <div style={{ height: '100vh', width: '100vw', position: 'relative' }}>
      
      {/* PANEL DE CONTROL FLOTANTE (ARRIBA IZQUIERDA) */}
      <div style={{
        position: 'absolute',
        top: '20px',
        left: '20px',
        zIndex: 1000,
        background: 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(10px)',
        padding: '20px',
        borderRadius: '15px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        width: '320px',
        border: '1px solid rgba(255,255,255,0.3)',
        display: 'flex',
        flexDirection: 'column',
        gap: '15px'
      }}>
        <h4 style={{ margin: 0, fontSize: '1.2rem', color: '#333', fontWeight: 'bold' }}>Panel Admin 🛠️</h4>
        
        <Form.Group>
          <Form.Label style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#666' }}>TIPO DE ELEMENTO</Form.Label>
          <Form.Select
            size="sm"
            value={selectedMarkerType}
            onChange={e => setSelectedMarkerType(e.target.value)}
            style={{ borderRadius: '8px' }}
          >
            <option value="alerta">🚨 Alerta</option>
            <option value="puntoRecogida">📍 Punto de Recogida</option>
            <option value="hotel">🏨 Hotel</option>
            <option value="hotel_vial">🛣️ Hotel En Vial</option>
            <option value="flecha">➡️ Flecha</option>
          </Form.Select>
        </Form.Group>

        <Form.Group>
          <Form.Label style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#666' }}>COLOR DE RUTA</Form.Label>
          <Form.Select
            size="sm"
            value={selectedLineColor}
            onChange={e => setSelectedLineColor(e.target.value)}
            style={{ borderRadius: '8px' }}
          >
            <option value="green">🟢 Verde (Segura)</option>
            <option value="yellow">🟡 Amarilla (Aviso)</option>
            <option value="red">🔴 Roja (Prohibida)</option>
            <option value="blue">🔵 Azul (Info)</option>
          </Form.Select>
        </Form.Group>

        <hr style={{ margin: '5px 0', opacity: 0.1 }} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Button variant="primary" size="sm" onClick={handleExportJSON} style={{ borderRadius: '8px' }}>
            💾 Descargar mapData.json
          </Button>
          
          <Button 
            variant="warning" 
            size="sm" 
            onClick={handleMigrateData} 
            disabled={isMigrating}
            style={{ borderRadius: '8px' }}
          >
            {isMigrating ? `⏳ ${migrationProgress}/${migrationTotal}` : '⚡ Migración Rápida JSON'}
          </Button>
        </div>

        <Button variant="outline-danger" size="sm" onClick={handleLogout} style={{ marginTop: '10px', borderRadius: '8px' }}>
          🔒 Cerrar Sesión
        </Button>

        {isMigrating && (
          <div style={{ width: '100%', height: '4px', background: '#eee', borderRadius: '2px', marginTop: '5px' }}>
            <div style={{ 
              height: '100%', 
              background: '#ffc107', 
              width: `${(migrationProgress/migrationTotal)*100}%`,
              transition: 'width 0.3s ease',
              borderRadius: '2px'
            }} />
          </div>
        )}
      </div>

      <MapContainer center={center} zoom={10} style={{ height: '100%', width: '100%' }}>
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap contributors"
        />
        <FeatureGroup ref={featureGroupRef}>
          <EditControl
            position="topright"
            onCreated={onCreated}
            draw={{
              rectangle: false,
              circle: false,
              circlemarker: false,
              polygon: false,
              marker: selectedMarkerType !== 'flecha',
              polyline: {
                shapeOptions: {
                  color: selectedMarkerType === 'flecha' ? 'black' : selectedLineColor,
                  dashArray: selectedMarkerType === 'flecha' ? '5,10' : undefined,
                  weight: selectedMarkerType === 'flecha' ? 6 : 3,
                  interactive: true
                }
              }
            }}
            edit={{ remove: false, edit: false }}
          />
        </FeatureGroup>

        {rutas.map(r => (
          <Polyline
            key={r.id}
            positions={r.coordenadas.map(c => [c.lat, c.lng])}
            color={getColor(r.tipo)}
          >
            <Popup>
              <strong>Ruta:</strong> {r.tipo}<br/>
              <Button size="sm" variant="danger" onClick={() => deleteDoc(doc(db, 'rutas', r.id))}>
                Eliminar
              </Button>
            </Popup>
          </Polyline>
        ))}

        {alertas.map(a => (
          <Marker
            key={a.id}
            position={[a.coordenadas.lat, a.coordenadas.lng]}
            icon={a.tipo === 'puntoRecogida' ? puntoRecogidaIcon : alertaIcon}
          >
            <Popup>
              <strong>{a.title}</strong><br/>
              <p>{a.description}</p>
              <Button size="sm" variant="danger" onClick={() => deleteDoc(doc(db, 'alertas', a.id))}>
                Eliminar
              </Button>
            </Popup>
          </Marker>
        ))}

        {hoteles.map(h => (
          <Marker
            key={h.id}
            position={[h.lat, h.lng]}
            icon={h.tipo === 'hotel_vial' ? hotelVialIcon : hotelIcon}
            draggable
            eventHandlers={{
              dragend: e => {
                const { lat, lng } = e.target.getLatLng();
                updateDoc(doc(db, 'hoteles', h.id), { lat, lng });
              }
            }}
          >
            <Popup>
              <strong>{h.nombre}</strong><br/>
              <Button size="sm" variant="danger" onClick={() => deleteDoc(doc(db, 'hoteles', h.id))}>
                Eliminar
              </Button>
            </Popup>
          </Marker>
        ))}

        {direcciones.map(d => {
          const positions = d.coords.map(c => [c.lat, c.lng]);
          return (
            <Fragment key={d.id}>
              <Polyline
                positions={positions}
                pathOptions={{ color: 'black', dashArray: '5,10', weight: 6, interactive: true }}
              >
                <Popup>
                  <Button size="sm" variant="danger" onClick={() => handleDeleteFlecha(d.id)}>
                    Eliminar flecha
                  </Button>
                </Popup>
              </Polyline>
              <ArrowedLine positions={positions} />
            </Fragment>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default AdminPanel;